import { TokenChainAvatar } from '@components/ChainLogo'
import { LineChart } from '@components/LineChart'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { UserActivity } from '@modules/UserActivity'
import { CKBTC_ICP_ID, ODINBTC_ID, TCYCLES_ICP_ID } from '@plug-core/constants/tokenIds'
import useBalances from '@plug-core/hooks/useBalances'
import useToken from '@plug-core/hooks/useToken'
import useTokenPriceInfo, { Range } from '@plug-core/hooks/useTokenPriceInfo'
import useWallet from '@plug-core/hooks/useWallet'
import { PlugToken } from '@plug-core/interfaces/common'
import { type PriceQuote, TimeFrame } from '@plug-core/interfaces/tokenPriceInfo'
import { useLocales, useUserTokens } from '@plug-core/state'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { Button, cn, Heading, IconButton, SegmentedControl, Spinner, Surface, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiArrowsUpDown, HiArrowUpRight, HiMiniStar } from 'react-icons/hi2'
import { IoIosSend } from 'react-icons/io'
import { useNavigate, useParams } from 'react-router-dom'

const RANGES: Range[] = [
  { value: 24 * 60, label: '1D', frame: TimeFrame.ONE_HOUR, dateFormat: 'HH:mm' },
  { value: 7 * 24 * 60, label: '1W', frame: TimeFrame.ONE_HOUR, dateFormat: 'd MMM yyyy' },
  { value: 30 * 24 * 60, label: '1M', frame: TimeFrame.ONE_DAY, dateFormat: 'd MMM yyyy' },
]

const TokenPriceChart = ({
  priceQuotes,
  onMouseMove,
  isLoading,
  dateFormat,
}: {
  priceQuotes: PriceQuote[]
  onMouseMove: (point?: PriceQuote) => void
  isLoading: boolean
  dateFormat: string
}) => {
  const { t } = useTranslation()
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)

  // The chart spans the full content width; re-measure when the layout changes.
  useEffect(() => {
    if (!container) return
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)))
    observer.observe(container)
    return () => observer.disconnect()
  }, [container])

  if (isLoading) {
    return <Spinner size={28} />
  } else if (priceQuotes?.length && priceQuotes.some((pq) => pq.price)) {
    return (
      <div ref={setContainer} className="w-full">
        {width > 0 && (
          <LineChart
            data={priceQuotes}
            width={width}
            height={200}
            paddingY={40}
            onMouseMove={onMouseMove}
            dateFormat={dateFormat}
          />
        )}
      </div>
    )
  } else {
    return (
      <div className="flex flex-col items-center gap-2">
        <span aria-hidden="true" className="text-[24px] leading-none">
          😔
        </span>
        <Text tone="fg-2" align="center">
          {t('tokens.unavailableAnalytics')}
        </Text>
      </div>
    )
  }
}

export const TokenDetails = () => {
  const [chosenRange, setChosenRange] = useState<Range>(
    RANGES.find((r) => r.label === '1W') || RANGES[0]
  )
  const [selectedPrice, setSelectedPrice] = useState<PriceQuote>()

  const { savedTokens, toggleToken } = useUserTokens()
  const { wallet } = useWallet()
  const { t } = useTranslation()
  const { userLocale } = useLocales()
  const { findBalance, refreshBalance } = useBalances()
  const navigate = useNavigate()
  const { tokenId } = useParams<{ tokenId: string }>()
  const token = useToken({ id: tokenId })

  const {
    priceQuotes: normalizedPriceQuotes,
    price,
    isLoading,
  } = useTokenPriceInfo({
    tokenId,
    range: chosenRange,
  })

  const priceQuotes = normalizedPriceQuotes.map((pq) => ({
    ...pq,
    price: pq.price * userLocale.currency.usdExchangeRate,
  }))

  const balance = findBalance(token)

  useEffect(() => {
    refreshBalance(token as PlugToken)
  }, [token.id])

  const getPriceChange = useCallback((currentPrice: PriceQuote, firstPoint: PriceQuote) => {
    return ((currentPrice.price - firstPoint.price) / firstPoint.price) * 100
  }, [])

  const priceChange = useMemo(() => {
    if (priceQuotes.length < 2) return undefined

    const firstPoint = priceQuotes[0]

    if (selectedPrice) {
      return getPriceChange(selectedPrice, firstPoint)
    }

    return getPriceChange(priceQuotes[priceQuotes.length - 1], firstPoint)
  }, [selectedPrice, getPriceChange, priceQuotes])

  const priceChangeFormatted = useMemo(() => {
    if (typeof priceChange !== 'number') return

    return `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)} %`
  }, [priceChange])

  const isFavorite = useMemo(() => {
    return (savedTokens[wallet.principalId] || []).includes(tokenId || '')
  }, [savedTokens, wallet.principalId, tokenId])

  const changeTone = (positive: boolean) => (positive ? 'text-success' : 'text-danger')

  const renderTokenDetails = () => {
    return (
      <Surface className="flex min-h-[72px] items-center gap-3">
        <TokenChainAvatar logo={token.logo} chains={[token.chain]} size={44} />
        <div className="flex min-w-0 flex-1 flex-col">
          <Heading
            level={3}
            tabular
            truncate
            className={cn(
              'text-[22px]',
              selectedPrice && changeTone(selectedPrice.price > priceQuotes[0].price)
            )}
          >
            {formatMoney(selectedPrice?.price || price, undefined, { format: 'precise' })}
          </Heading>
          <Text variant="body-s" tone="fg-2">
            {token.symbol}
          </Text>
        </div>
        {typeof priceChange !== 'undefined' && (
          <Text
            as="span"
            weight="semibold"
            tabular
            className={cn('shrink-0', changeTone(priceChange > 0))}
          >
            {priceChangeFormatted}
          </Text>
        )}
      </Surface>
    )
  }

  const renderBalance = () => {
    return (
      <Surface className="flex min-h-[72px] items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text variant="caption" tone="fg-2">
            Balance
          </Text>
          <Heading level={3} tabular truncate className="text-[20px]">
            {formatNumber(
              e8sToDecimals(balance?.balance || 0n, token.decimals!),
              token.symbol?.toUpperCase()
            )}
          </Heading>
        </div>
        <div className="flex min-w-0 flex-col items-end gap-0.5">
          <Text variant="caption" tone="fg-2">
            Value
          </Text>
          <Heading level={3} tabular truncate className="text-[20px]">
            {formatMoney(balance?.usdBalance)}
          </Heading>
        </div>
      </Surface>
    )
  }

  const renderFavoriteIcon = () => (
    <IconButton
      aria-label={isFavorite ? t('tokenDetails.removeFavorite') : t('tokenDetails.addFavorite')}
      aria-pressed={isFavorite}
      className={cn(isFavorite ? 'text-yellow' : 'text-fg')}
      onClick={() => toggleToken(wallet.principalId, tokenId!)}
    >
      <HiMiniStar />
    </IconButton>
  )

  const ckmintSymbol = useToken({ id: token.additionalData?.ckmint })?.symbol

  return (
    <div className="mb-3 flex flex-col">
      <Titlebar onActionClick={() => navigate(-1)} leftItem={renderFavoriteIcon()} />
      <div className="flex flex-col gap-4">
        {renderTokenDetails()}
        <div className="flex h-[220px] w-full flex-col items-center justify-center">
          <TokenPriceChart
            isLoading={isLoading}
            priceQuotes={priceQuotes}
            dateFormat={chosenRange.dateFormat}
            onMouseMove={(point) =>
              setSelectedPrice(
                point
                  ? {
                      timestamp: point.timestamp,
                      price: point.price,
                    }
                  : undefined
              )
            }
          />
        </div>
        <div className="flex justify-center">
          <SegmentedControl
            aria-label={t('tokenDetails.chartRange')}
            size="sm"
            value={chosenRange.label}
            onChange={(label) => setChosenRange(RANGES.find((r) => r.label === label) || RANGES[0])}
            options={RANGES.map((range) => ({ value: range.label, label: range.label }))}
          />
        </div>
        {renderBalance()}
        {token.additionalData?.ckmint && (
          <Button
            variant="secondary"
            startIcon={<HiArrowUpRight />}
            onClick={() =>
              navigate(ROUTES.SWAP, {
                state: { fromTokenId: token.id, toTokenId: token.additionalData?.ckmint },
              })
            }
            fullWidth
          >
            Convert to {ckmintSymbol}
          </Button>
        )}
        {token.id === CKBTC_ICP_ID && (
          <Button
            variant="secondary"
            startIcon={<HiArrowUpRight />}
            onClick={() =>
              navigate(ROUTES.SWAP, { state: { fromTokenId: token.id, toTokenId: ODINBTC_ID } })
            }
            fullWidth
          >
            {t('tokenDetails.depositToOdin')}
          </Button>
        )}
        {token.id === TCYCLES_ICP_ID && (
          <Button
            variant="secondary"
            startIcon={<HiArrowUpRight />}
            onClick={() => navigate(ROUTES.TOPUP)}
            fullWidth
          >
            {t('tokenDetails.topUpCanisters')}
          </Button>
        )}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            startIcon={<HiArrowsUpDown />}
            onClick={() => navigate(ROUTES.SWAP, { state: { fromTokenId: token.id } })}
            fullWidth
            className="flex-1"
          >
            Swap
          </Button>

          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.SEND, { state: { selectedTokenId: tokenId } })}
            startIcon={<IoIosSend />}
            fullWidth
            className="flex-1"
          >
            Send
          </Button>
        </div>

        {token.description && (
          <div className="flex flex-col gap-1">
            <Heading level={5}>{t('tokenDetails.description')}</Heading>
            <Text variant="body-s" tone="fg-2">
              {token.description}
            </Text>
          </div>
        )}

        <UserActivity filter={{ token_id: tokenId }} nested hideEmpty />
      </div>
    </div>
  )
}
